// Logger service that sends console logs to server in development/staging
class LoggerService {
  // Enable logging in dev mode OR when VITE_DEBUG is explicitly true
  private isDevelopment = import.meta.env.DEV || import.meta.env.VITE_DEBUG === 'true';
  private logEndpoint = '/api/logs';
  private logQueue: any[] = [];
  private isProcessing = false;
  private originalConsole = {
    log: console.log.bind(console),
    error: console.error.bind(console),
    warn: console.warn.bind(console),
    info: console.info.bind(console),
    debug: console.debug.bind(console),
  };
  private originalFetch = window.fetch.bind(window);
  private originalXHROpen = XMLHttpRequest.prototype.open.bind(XMLHttpRequest.prototype);

  constructor() {
    if (this.isDevelopment) {
      this.initializeLogging();
    }
  }

  private initializeLogging() {
    // Override console methods to capture logs
    console.log = (...args) => {
      this.originalConsole.log(...args);
      this.sendToServer('log', args);
    };

    console.error = (...args) => {
      this.originalConsole.error(...args);
      this.sendToServer('error', args);
    };

    console.warn = (...args) => {
      this.originalConsole.warn(...args);
      this.sendToServer('warn', args);
    };

    console.info = (...args) => {
      this.originalConsole.info(...args);
      this.sendToServer('info', args);
    };

    console.debug = (...args) => {
      this.originalConsole.debug(...args);
      this.sendToServer('debug', args);
    };

    // Capture unhandled errors
    window.addEventListener('error', (event) => {
      this.sendToServer('error', [`Unhandled Error: ${event.message}`, event.error]);
    });

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.sendToServer('error', [`Unhandled Promise Rejection: ${event.reason}`]);
    });

    // Capture network and resource loading errors
    this.setupNetworkErrorHandling();
    this.setupResourceErrorHandling();
    this.setupFetchInterception();
    this.setupXHRInterception();
  }

  private setupNetworkErrorHandling() {
    // Capture network errors from image, script, and other resource loads
    window.addEventListener('error', (event) => {
      if (event.target !== window) {
        const target = event.target as HTMLElement;
        const tagName = target.tagName?.toLowerCase();
        const src = (target as any).src || (target as any).href;
        
        this.sendToServer('error', [
          `Resource Loading Error [${tagName}]: ${src}`,
          {
            type: 'resource_error',
            tagName,
            src,
            message: event.message || 'Failed to load resource'
          }
        ]);
      }
    }, true); // Use capture phase to catch all events
  }

  private setupResourceErrorHandling() {
    // Additional resource error handling for specific elements
    const handleResourceError = (event: Event) => {
      const target = event.target as HTMLElement;
      const tagName = target.tagName?.toLowerCase();
      const src = (target as any).src || (target as any).href;
      
      this.sendToServer('error', [
        `Resource Failed to Load [${tagName}]: ${src}`,
        {
          type: 'resource_load_error',
          tagName,
          src,
          timestamp: new Date().toISOString()
        }
      ]);
    };

    // Add error listeners to dynamically created elements
    document.addEventListener('error', handleResourceError, true);
  }

  private setupFetchInterception() {
    // Intercept fetch API to capture network errors and CORS issues
    window.fetch = async (...args) => {
      const [resource, config] = args;
      const url = typeof resource === 'string' ? resource : 
                  resource instanceof Request ? resource.url : 
                  resource instanceof URL ? resource.toString() : 
                  String(resource);
      
      try {
        const response = await this.originalFetch(...args);
        
        // Log failed HTTP responses
        if (!response.ok) {
          this.sendToServer('error', [
            `Fetch Error [${response.status}]: ${url}`,
            {
              type: 'fetch_error',
              url,
              status: response.status,
              statusText: response.statusText,
              headers: Object.fromEntries(response.headers.entries()),
              method: config?.method || 'GET'
            }
          ]);
        }
        
        return response;
      } catch (error) {
        // Capture network errors, CORS errors, etc.
        const errorInfo = {
          type: 'fetch_network_error',
          url,
          method: config?.method || 'GET',
          error: error instanceof Error ? error.message : String(error),
          name: error instanceof Error ? error.name : 'NetworkError'
        };

        // Special handling for common network errors
        if (error instanceof TypeError) {
          if (error.message.includes('CORS')) {
            errorInfo.type = 'cors_error';
            this.sendToServer('error', [
              `CORS Error: ${url}`,
              errorInfo
            ]);
          } else if (error.message.includes('Failed to fetch')) {
            errorInfo.type = 'network_error';
            this.sendToServer('error', [
              `Network Error: ${url}`,
              errorInfo
            ]);
          } else {
            this.sendToServer('error', [
              `Fetch TypeError: ${url}`,
              errorInfo
            ]);
          }
        } else {
          this.sendToServer('error', [
            `Fetch Error: ${url}`,
            errorInfo
          ]);
        }
        
        throw error; // Re-throw to maintain original behavior
      }
    };
  }

  private setupXHRInterception() {
    // Store the original method first
    const originalOpen = XMLHttpRequest.prototype.open;
    
    // Intercept XMLHttpRequest to capture additional network errors
    XMLHttpRequest.prototype.open = function(method: string, url: string | URL, async?: boolean, user?: string | null, password?: string | null) {
      // Set up error handlers only for this instance
      const xhr = this;
      
      // Only add listeners if they haven't been added already
      if (!(xhr as any)._loggerListenersAdded) {
        xhr.addEventListener('error', () => {
          loggerService.sendToServer('error', [
            `XHR Error [${method}]: ${url}`,
            {
              type: 'xhr_error',
              method,
              url: url.toString(),
              readyState: xhr.readyState,
              status: xhr.status,
              statusText: xhr.statusText
            }
          ]);
        });

        xhr.addEventListener('timeout', () => {
          loggerService.sendToServer('error', [
            `XHR Timeout [${method}]: ${url}`,
            {
              type: 'xhr_timeout',
              method,
              url: url.toString(),
              timeout: xhr.timeout
            }
          ]);
        });

        xhr.addEventListener('abort', () => {
          loggerService.sendToServer('error', [
            `XHR Aborted [${method}]: ${url}`,
            {
              type: 'xhr_abort',
              method,
              url: url.toString()
            }
          ]);
        });
        
        // Mark this instance as having listeners to avoid duplicates
        (xhr as any)._loggerListenersAdded = true;
      }

      // Call the original method with proper context and arguments
      return originalOpen.call(this, method, url, async ?? true, user, password);
    };
  }

  private sendToServer(level: string, args: any[]) {
    if (!this.isDevelopment) return;

    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      message: this.formatMessage(args),
      args: this.serializeArgs(args),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    this.logQueue.push(logEntry);
    this.processQueue();
  }

  private formatMessage(args: any[]): string {
    return args.map(arg => {
      if (typeof arg === 'string') return arg;
      
      // Handle Error objects using the same logic as serializeArgs
      if (arg instanceof Error || (arg && arg.constructor && arg.constructor.name === 'Error') || (arg && typeof arg.message === 'string' && typeof arg.stack === 'string')) {
        const errorInfo = {
          _errorType: 'Error',
          name: arg.name || 'Error',
          message: arg.message || '',
          stack: arg.stack || '',
          toString: arg.toString ? arg.toString() : String(arg)
        };
        try {
          return JSON.stringify(errorInfo, null, 2);
        } catch (e) {
          return `[Error: ${arg.message || 'Unknown error'}]`;
        }
      }
      
      if (typeof arg === 'object') {
        try {
          return JSON.stringify(arg, null, 2);
        } catch (e) {
          return '[Object object]';
        }
      }
      return String(arg);
    }).join(' ');
  }

  private serializeArgs(args: any[]): any[] {
    const serializeValue = (value: any): any => {
      // Handle Error objects (including nested ones)
      if (value instanceof Error || (value && value.constructor && value.constructor.name === 'Error') || (value && typeof value.message === 'string' && typeof value.stack === 'string')) {
        return {
          _errorType: 'Error',
          name: value.name || 'Error',
          message: value.message || '',
          stack: value.stack || '',
          toString: value.toString ? value.toString() : String(value)
        };
      }
      
      // Handle functions
      if (typeof value === 'function') {
        return '[Function]';
      }
      
      // Handle DOM elements
      if (value instanceof Element) {
        return `[${value.tagName}]`;
      }
      
      // Handle null and undefined
      if (value === null) return null;
      if (value === undefined) return undefined;
      
      // Handle primitives
      if (typeof value !== 'object') {
        return value;
      }
      
      // Handle arrays
      if (Array.isArray(value)) {
        return value.map(serializeValue);
      }
      
      // Handle objects
      try {
        const result: any = {};
        for (const [key, val] of Object.entries(value)) {
          result[key] = serializeValue(val);
        }
        return result;
      } catch (e) {
        return '[Complex object - serialization failed]';
      }
    };

    return args.map(serializeValue);
  }

  private async processQueue() {
    if (this.isProcessing || this.logQueue.length === 0) return;

    this.isProcessing = true;
    const logsToSend = this.logQueue.splice(0, 10); // Send up to 10 logs at a time

    try {
      await this.originalFetch(this.logEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ logs: logsToSend }),
      });
    } catch (error) {
      // Don't log this error to avoid infinite loops
      this.originalConsole.error('Failed to send logs to server:', error);
    } finally {
      this.isProcessing = false;
      
      // Process remaining logs if any
      if (this.logQueue.length > 0) {
        setTimeout(() => this.processQueue(), 100);
      }
    }
  }

  // Method to send custom logs without affecting console
  public sendLog(level: 'log' | 'error' | 'warn' | 'info' | 'debug', ...args: any[]) {
    if (this.isDevelopment) {
      this.sendToServer(level, args);
    }
  }

  // Method to restore original console (for cleanup)
  public restoreConsole() {
    if (this.isDevelopment) {
      console.log = this.originalConsole.log;
      console.error = this.originalConsole.error;
      console.warn = this.originalConsole.warn;
      console.info = this.originalConsole.info;
      console.debug = this.originalConsole.debug;
      window.fetch = this.originalFetch;
      XMLHttpRequest.prototype.open = this.originalXHROpen;
    }
  }
}

// Export singleton instance
export const loggerService = new LoggerService();
export default loggerService; 